/**
 * Auswertung: durchschnittliches Trinkgeld je Monat und Jahr.
 *
 * Für die Frage, die der Buchhaltung oft gestellt wird: wie viel bekommt man
 * hier im Schnitt? Die vergleichbarste Antwort ist der Betrag pro Stunde, weil
 * er nicht davon abhängt, wie viel jemand arbeitet; daneben pro Abend und pro
 * Monat, weil danach meist gefragt wird. Gerechnet wird in der API
 * (`services/auswertung.ts`), nur über ausgezahlte Tage.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type AuswertungDto, type DurchschnittDto } from "../lib/api";
import { euro, monthName, shortDate } from "../lib/format";
import { Empty, ErrorBox, Info, PageHeader, Section, Spinner, Stat } from "../components/ui";

export function Auswertung() {
  const [jahr, setJahr] = useState<string | null>(null);
  const auswertung = useQuery({
    queryKey: ["auswertung", jahr],
    queryFn: () => api.get<AuswertungDto>(`/auswertung${jahr ? `?jahr=${jahr}` : ""}`),
  });

  if (auswertung.isLoading) return <Spinner label="Auswertung wird geladen" />;
  if (auswertung.error) return <ErrorBox error={auswertung.error} />;
  const a = auswertung.data;
  if (!a) return null;
  const g = a.gesamt;
  const ohneStunden = a.monate.some((m) => m.proStunde === null);
  const teilweise = a.monate.some((m) => m.proStunde !== null && !m.stundenVollstaendig);

  return (
    <>
      <PageHeader
        label="Auswertung"
        title="Durchschnittliches Trinkgeld"
        actions={
          a.jahre.length > 1 ? (
            <select
              className="field w-auto"
              value={a.jahr}
              onChange={(e) => setJahr(e.target.value)}
              aria-label="Jahr"
            >
              {a.jahre.map((j) => (
                <option key={j} value={j}>
                  {j}
                </option>
              ))}
            </select>
          ) : null
        }
      >
        Für die Frage, wie viel man im Schnitt bekommt. Gezählt werden nur ausgezahlte Tage
        {a.stand ? `, Stand ${shortDate(a.stand)}${a.stand.slice(0, 4)}` : ""}.
      </PageHeader>

      {a.monate.length === 0 ? (
        <Empty>Für {a.jahr} gibt es noch keine ausgezahlten Tage.</Empty>
      ) : (
        <>
          <div className="mb-10 flex flex-wrap gap-6">
            <Stat
              label={`Ø pro Stunde ${a.jahr}`}
              value={euro(g.proStunde)}
              info="Trinkgeld geteilt durch die gearbeiteten Stunden. Die beste Antwort auf „Wie viel bekommt man?“, weil sie nicht davon abhängt, wie viel jemand arbeitet. Nur Monate, für die Stunden vorliegen."
            />
            <Stat
              label="Ø pro Abend und Person"
              value={euro(g.proAbendUndPerson)}
              info="Was eine Person an einem Abend im Schnitt bekommt. Wer an einem Abend in zwei Bereichen stand, zählt einmal."
            />
            <Stat
              label="Ø pro Person und Monat"
              value={euro(g.proPersonImMonat)}
              info="Was eine Person in einem Monat, in dem sie Trinkgeld bekommen hat, im Schnitt bekommt. Hängt stark davon ab, wie oft jemand arbeitet."
            />
            <Stat label={`Trinkgeld ${a.jahr}`} value={euro(g.trinkgeld)} />
          </div>

          <Section label="Je Monat" title={`${a.jahr} nach Monaten`}>
            <div className="card overflow-x-auto">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Monat</th>
                    <th className="text-right">Trinkgeld</th>
                    <th className="text-right">Abende</th>
                    <th className="text-right">Personen</th>
                    <th className="text-right">
                      Ø pro Stunde
                      <Info text="Trinkgeld geteilt durch die gearbeiteten Stunden. Für Monate aus der Excel-Übernahme gibt es keine Stunden." />
                    </th>
                    <th className="text-right">Ø pro Abend und Person</th>
                    <th className="text-right">Ø pro Person im Monat</th>
                  </tr>
                </thead>
                <tbody>
                  {a.monate.map((m) => (
                    <Zeile key={m.zeitraum} titel={monthName(m.zeitraum).split(" ")[0] ?? m.zeitraum} d={m} />
                  ))}
                  <Zeile titel={`Jahr ${a.jahr}`} d={g} summe />
                </tbody>
              </table>
            </div>
            {ohneStunden || teilweise ? (
              <p className="mt-3 text-xs text-muted">
                {ohneStunden
                  ? "„–“ bei Monaten aus der Excel-Übernahme: das Archiv kennt nur Person, Tag und Betrag, keine Stunden. "
                  : null}
                {teilweise || (ohneStunden && g.proStunde !== null)
                  ? "„teilweise“: Tage aus Excel und aus der App kommen zusammen; pro Stunde gilt nur für die Tage aus der App."
                  : null}
              </p>
            ) : null}
          </Section>
        </>
      )}
    </>
  );
}

function Zeile({ titel, d, summe = false }: { titel: string; d: DurchschnittDto; summe?: boolean }) {
  return (
    <tr className={summe ? "border-t-2 border-ink/20 font-medium" : undefined}>
      <td>{titel}</td>
      <td className="num">{euro(d.trinkgeld)}</td>
      <td className="num">{d.abende}</td>
      <td className="num">{d.personen}</td>
      <td className="num">
        {euro(d.proStunde)}
        {d.proStunde !== null && !d.stundenVollstaendig ? (
          <span className="ml-1 text-xs font-normal text-muted">teilweise</span>
        ) : null}
      </td>
      <td className="num">{euro(d.proAbendUndPerson)}</td>
      <td className="num">{euro(d.proPersonImMonat)}</td>
    </tr>
  );
}
